interface ISearchHit {
  categories?: ISearchHitCategory[];
  heading: string;
  url: string;
  excerpt: string | null;
  lastUpdatedDate: string;
  lastUpdatedDateISO: string;
  type: string;
  fileType: string | null;
}

interface ISearchHitCategory {
  id: number;
  name: string;
}

interface ISearchResult {
  searchHits: ISearchHit[];
  totalHits: number;
  filters: Record<string, string>;
  categoryFilters: Record<string, string>;
  filterCounts: Record<string, number>;
  categoryFilterCounts: Record<string, number>;
}

interface ITextContent {
  noKeywords: string;
  noResults: string;
  resultFor: string;
  resultsFor: string;
  lastUpdated: string;
  showMore: string;
}

const useSearchPage = (): void => {
  const searchForm = document.querySelector('.search-page__form') as HTMLFormElement;
  const searchInput = document.querySelector('.search-page__input') as HTMLInputElement;
  const searchResultSection = document.querySelector('.search-page__result') as HTMLElement;
  const searchResultContainer = document.querySelector('.search-page__result-container') as HTMLDivElement;
  const searchResultInfo = document.querySelector('.search-page__result-info') as HTMLParagraphElement;
  const spinner = document.querySelector('.spinner') as HTMLDivElement;

  /* Data */
  const { hitsToShow, hitsToAdd, currentLanguage, includeText } = searchForm.dataset;

  /* Text content (translated text) */
  let includeTextArray = includeText?.split(', ') as string[];
  let textContent: ITextContent = {
    noKeywords: includeTextArray[0],
    noResults: includeTextArray[1],
    resultFor: includeTextArray[2],
    resultsFor: includeTextArray[3],
    lastUpdated: includeTextArray[4],
    showMore: includeTextArray[5]
  };

  /* Global variables */
  let query: string | null = null;
  let apiSkipAmount: number = 0;
  let apiTakeAmount: number = parseInt(hitsToShow!, 10);
  let selectedFilterIds: number[] = [];
  let selectedCategoryFilterIds: number[] = [];

  /* Functions – Reset and clear DOM */
  const resetSearchResult = (): void => {
    searchInput.value = '';
    searchResultSection.classList.add('sr-only');
    searchResultInfo.textContent = textContent.noKeywords;
  }

  const clearResultList = (): void => {
    const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');
    const showMore: HTMLDivElement | null = document.querySelector('.show-more');

    if (resultList) {
      resultList.remove();
    }

    if (showMore) {
      showMore.remove();
    }
  }

  /* Functions – Get data from API */
  const getSearchResult = async (): Promise<ISearchResult | void> => {
    try {
      const params = new URLSearchParams({
        query: query ?? '',
        take: apiTakeAmount.toString(),
        skip: apiSkipAmount.toString(),
        lang: currentLanguage ?? '',
        filter: selectedFilterIds.join(','),
        categoryFilter: selectedCategoryFilterIds.join(',')
      });

      const response = await fetch(`/api/search?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return result;
    } catch (error) {
      console.log(error);
      
      spinner.classList.remove('spinner--show');
    }
  }

  /* Functions – Render DOM */
  const renderResultItems = (searchHits: ISearchHit[], resultList: HTMLUListElement): void => {
    searchHits.forEach(item => {      
      const listItem = document.createElement('li') as HTMLLIElement;
      listItem.classList.add('search-page__result-item', 'section__item', 'section__item--full', 'site-page-partial');

      if (item.type === 'Media') {
        listItem.classList.add('search-page__result-item--media');
      }
      
      listItem.addEventListener('click', (): void => {
        window.location.href = item.url;
      });
      
      const content = document.createElement('div') as HTMLDivElement;
      content.classList.add('site-page-partial__content', 'search-page__result-item-content');
      listItem.appendChild(content);
      
      const textContainer = document.createElement('div') as HTMLDivElement;
      textContainer.classList.add('site-page-partial__text-container', 'search-page__result-item-text-container');
      content.appendChild(textContainer);

      if (item.categories && item.categories.length > 0) {
        const categoriesList = document.createElement('ul') as HTMLUListElement;
        categoriesList.classList.add('site-page-partial__category-list', 'tag-container');

        item.categories.forEach((category) => {
          const categoryItem = document.createElement('li') as HTMLLIElement;
          categoryItem.classList.add('site-page-partial__category-item', 'tag', 'tag--small');
          categoryItem.textContent = category.name;
          categoriesList.appendChild(categoryItem);
        });

        textContainer.appendChild(categoriesList);
      }

      const heading = document.createElement('h3') as HTMLHeadingElement;
      heading.classList.add(
        'site-page-partial__heading',
        'heading',
        'heading--small',
        'search-page__result-item-heading'
      );
      textContainer.appendChild(heading);

      if (item.type === 'Media') {
        const documentIcon = document.createElement('span') as HTMLSpanElement;
        documentIcon.classList.add('site-page-partial__heading-icon', 'heading__icon', 'icon', 'icon--file');
        heading.classList.add('heading--has-icon');
        heading.appendChild(documentIcon);
      }
      
      const link = document.createElement('a') as HTMLAnchorElement;
      link.classList.add('site-page-partial__link', 'heading__text');
      link.setAttribute('href', item.url);
      link.textContent = item.heading;

      if (item.fileType) {
        link.textContent += ` (${item.fileType})`;
      }
      
      heading.appendChild(link);

      if (item.excerpt) {
        const excerpt = document.createElement('p') as HTMLParagraphElement;
        excerpt.classList.add('site-page-partial__excerpt');
        excerpt.textContent = item.excerpt;
        textContainer.appendChild(excerpt);
      }
      
      const lastUpdatedDate = document.createElement('span') as HTMLSpanElement;
      lastUpdatedDate.classList.add('site-page-partial__date');
      lastUpdatedDate.textContent = `${textContent.lastUpdated}: `
      textContainer.appendChild(lastUpdatedDate);

      const time = document.createElement('time') as HTMLTimeElement;
      time.setAttribute('datetime', item.lastUpdatedDateISO);
      time.textContent = item.lastUpdatedDate;
      lastUpdatedDate.appendChild(time);
      
      const arrowIconContainer = document.createElement('div') as HTMLDivElement;
      arrowIconContainer.classList.add('site-page-partial__arrow-icon-container');
      textContainer.appendChild(arrowIconContainer);
      
      const arrowIcon = document.createElement('span') as HTMLSpanElement;
      if(item.type === 'Media') {
        arrowIcon.classList.add('site-page-partial__arrow-icon', 'icon', 'icon--arrow-down');
      } else {
        arrowIcon.classList.add('site-page-partial__arrow-icon', 'icon', 'icon--arrow-right');        
      }

      arrowIconContainer.appendChild(arrowIcon);

      resultList.appendChild(listItem);
    });
  }

  const renderShowMoreButton = (): void => {
    const showMoreContainer = document.createElement('div');
    showMoreContainer.classList.add('show-more');

    const showMoreButton = document.createElement('button');
    showMoreButton.classList.add('search-page__show-more-button', 'button', 'button--secondary');
    showMoreButton.setAttribute('type', 'button');
    showMoreButton.innerHTML = `<span class="button__icon icon icon--plus"></span> ${textContent.showMore}`;

    showMoreButton.addEventListener('click', async(): Promise<void> => {
      await addMoreResultItems(showMoreButton);
    });

    showMoreContainer.appendChild(showMoreButton);
    searchResultSection.appendChild(showMoreContainer);
  }

  const renderResult = (searchResult: ISearchResult): void => {
    clearResultList();

    if (searchResult.totalHits > 0) {      
      const searchResultSectionContainer = searchResultSection.querySelector('.section__container') as HTMLDivElement;
      const resultList = document.createElement('ul') as HTMLUListElement;
      resultList.classList.add('search-page__result-list', 'section__content');
      searchResultSectionContainer.appendChild(resultList);

      renderResultItems(searchResult.searchHits, resultList);
      let filterElement = (document.querySelector('.filter') as HTMLDivElement);
      let filterListElement = (document.querySelector('.search-page__filter-list') as HTMLUListElement);
      let categoryFilterListElement = (document.querySelector('.search-page__category-filter-list') as HTMLUListElement);
      
      const hasFilters = searchResult.filters && Object.keys(searchResult.filters).length > 0;
      const hasCategoryFilters = searchResult.categoryFilters && Object.keys(searchResult.categoryFilters).length > 0;
      const formatFilterName = (name: string, filterId: string, counts: Record<string, number>): string => {
        const count = counts?.[filterId];
        if (count === undefined) {
          return name;
        }

        return `${name} (${count})`;
      };
      const renderFilterButtons = (
        listElement: HTMLUListElement,
        filters: Record<string, string>,
        selectedIds: number[],
        counts: Record<string, number>,
        buttonTypeClass: string
      ): void => {
        listElement.textContent = '';

        Object.entries(filters)
          .sort(([_, name1], [__, name2]) => name1.localeCompare(name2, currentLanguage))
          .forEach(([key, value]) => {
            const listItem = document.createElement('li') as HTMLLIElement;
            const button = document.createElement('button') as HTMLButtonElement;
            const isSelected = selectedIds.includes(parseInt(key, 10));

            button.classList.add('button', 'button--filter', buttonTypeClass);
            if (isSelected) {
              button.classList.add('button--selected');
            }

            button.dataset.id = key;
            button.textContent = formatFilterName(value, key, counts);

            listItem.appendChild(button);
            listElement.appendChild(listItem);
          });
      };

      // Render existing content/section filters
      if (hasFilters && !!filterListElement) {
        renderFilterButtons(
          filterListElement,
          searchResult.filters,
          selectedFilterIds,
          searchResult.filterCounts,
          'button--section-filter'
        );
      } else if (filterListElement) {
        filterListElement.textContent = '';
      }

      // Render category filters
      if (hasCategoryFilters && !!categoryFilterListElement) {
        renderFilterButtons(
          categoryFilterListElement,
          searchResult.categoryFilters,
          selectedCategoryFilterIds,
          searchResult.categoryFilterCounts,
          'button--category-filter'
        );
      } else if (categoryFilterListElement) {
        categoryFilterListElement.textContent = '';
      }

      if (hasFilters || hasCategoryFilters) {
        filterElement.hidden = false;
      } else {
        filterElement.hidden = true;
      }

      let clearFilterButton = document.querySelector('.search-page__filter-clear-button') as HTMLButtonElement;

      clearFilterButton.hidden = selectedFilterIds.length <= 0 && selectedCategoryFilterIds.length <= 0;

      if (searchResult.totalHits === 1) {
        searchResultInfo.innerHTML = `${searchResult.totalHits} ${textContent.resultFor} <strong>${query}</strong>.`;
      } else {
        searchResultInfo.innerHTML = `${searchResult.totalHits} ${textContent.resultsFor} <strong>${query}</strong>.`;
      }
      
      // Add search filter event listeners
      addFilterButtonsEventListeners();

      if (searchResult.searchHits.length < searchResult.totalHits) {
        renderShowMoreButton();
      }
    } else {
      searchResultInfo.textContent = textContent.noResults;
    }
  }

  const renderWithoutResult = (): void => {
    clearResultList();

    let filterElement: HTMLDivElement | null = (document.querySelector('.filter') as HTMLDivElement);
    let filterListElement: HTMLUListElement | null = (document.querySelector('.search-page__filter-list') as HTMLUListElement);
    let categoryFilterListElement: HTMLUListElement | null = (document.querySelector('.search-page__category-filter-list') as HTMLUListElement);
    let clearFilterButton: HTMLButtonElement = document.querySelector('.search-page__filter-clear-button') as HTMLButtonElement;
    
    // Remove filters
    if (filterElement) {
      filterElement.hidden = true;
      filterListElement.innerHTML = '';
      categoryFilterListElement.innerHTML = '';
    }
    
    // Hide clear filter button
    clearFilterButton.hidden = true;

    // Reset search result
    searchInput.value = '';
    searchResultInfo.textContent = textContent.noKeywords;
  }

  /* Functions - Search */
  const search = async(): Promise<void> => {
    apiSkipAmount = 0;

    searchResultContainer.style.opacity = '0';
    searchResultSection.classList.remove('sr-only');
    spinner.classList.add('spinner--show');

    let searchResult = await getSearchResult();

    setTimeout(() => {
      spinner.classList.remove('spinner--show');
      searchResultContainer.removeAttribute('style');

      if (searchResult) {
        renderResult(searchResult);
      }
    }, 1000)
  }

  const searchFromQueryString = async(): Promise<void> => {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    let queryFromParam: string | null = urlParams.get('query');
    let takeFromParam: string | null = urlParams.get('take');
    let selectedFilterIdsFromParam: string | null = urlParams.get('filter');
    let selectedCategoryFilterIdsFromParam: string | null = urlParams.get('categoryFilter');

    query = queryFromParam;

    if (query) {
      query = query.trim();
      searchInput.value = query;
    } else {
      query = '';
    }

    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
      apiTakeAmount = parseInt(takeFromParam, 10);
    }
    
    if (!!selectedFilterIdsFromParam?.length) {
      selectedFilterIds = selectedFilterIdsFromParam.split(',').map(Number);
    } else {
      selectedFilterIds = [];
    }

    if (!!selectedCategoryFilterIdsFromParam?.length) {
      selectedCategoryFilterIds = selectedCategoryFilterIdsFromParam.split(',').map(Number);
    } else {
      selectedCategoryFilterIds = [];
    }

    if (query.length > 0) {
      await search();
    } else {
      renderWithoutResult();
    }
  }

  const addMoreResultItems = async (showMoreButton: HTMLButtonElement): Promise<void> => {
    apiSkipAmount += apiTakeAmount;
    apiTakeAmount = parseInt(hitsToAdd!, 10);

    let searchResult = await getSearchResult();
    if (searchResult) {
      const resultList = document.querySelector('.search-page__result-list') as HTMLUListElement;
      const resultItemsCount = resultList.children.length as number;
      let takeAmount = resultItemsCount + parseInt(hitsToAdd!, 10);

      if (searchResult.totalHits < takeAmount) {
        takeAmount = searchResult.totalHits;
      }

      const params = new URLSearchParams({
        query: query ?? '',
        take: takeAmount.toString()
      });

      if (selectedFilterIds.length > 0) {
        params.set('filter', selectedFilterIds.join(','));
      }

      if (selectedCategoryFilterIds.length > 0) {
        params.set('categoryFilter', selectedCategoryFilterIds.join(','));
      }

      window.history.replaceState({},'', `?${params.toString()}`);

      renderResultItems(searchResult.searchHits, resultList);

      if (resultList.children.length === searchResult.totalHits) {
        showMoreButton.parentElement!.remove();
      }

      let resultItems = resultList.children as HTMLCollection;
      let firstAddedResultItem = resultItems[resultItemsCount] as HTMLLIElement;
      let firstAddedResultItemLink = firstAddedResultItem.querySelector('a') as HTMLAnchorElement;
      firstAddedResultItemLink.focus();
    }
  }

  const getSelectedFilterButtons = (buttonSelector: string): number[] => {
    const selectedFilterButtons = document.querySelectorAll(buttonSelector) as NodeListOf<HTMLButtonElement>;
    
    return [...selectedFilterButtons].map((button) => parseInt(button.dataset.id!));
  };

  const handleFilterButtonEvent = async(event: MouseEvent): Promise<void> => {
    const button = event.currentTarget as HTMLButtonElement | null;
    if (!button) {
      return;
    }

    button.classList.toggle('button--selected');

    selectedFilterIds = getSelectedFilterButtons('.button--section-filter.button--selected');
    selectedCategoryFilterIds = getSelectedFilterButtons('.button--category-filter.button--selected');
    
    // Add filters to query string
    const params = new URLSearchParams();

    if (!!query) {
      params.set('query', query);
    }

    if (!!apiTakeAmount) {
      params.set('take', apiTakeAmount.toString());
    }

    if (selectedFilterIds.length > 0) {
      params.set('filter', selectedFilterIds.join(','));
    }

    if (selectedCategoryFilterIds.length > 0) {
      params.set('categoryFilter', selectedCategoryFilterIds.join(','));
    }

    window.history.pushState({},'', `?${params.toString()}`);
    
    // Clear search result
    clearResultList();

    // Show pages based on selected category id:s
    await search();
  };
  
  const addFilterButtonsEventListeners = (): void => {
    const filterButtons = document.querySelectorAll('.button--filter') as NodeListOf<HTMLButtonElement>
    
    if (filterButtons.length > 0) {
      filterButtons.forEach((button) => {
        button.addEventListener('click', handleFilterButtonEvent);
      });
    }
  }

  const initializeEvents = (): void => {
    // Event that happens if someone enters url with query string
    window.addEventListener('load', async(): Promise<void> => {
      if (window.location.search) {
        await searchFromQueryString();
      }

      searchInput.focus();
    });

    // Event that happens when users browse through their browser history, e.g. use the back button.
    window.addEventListener('popstate', async(): Promise<void> => {
      if (window.location.search) {
        await searchFromQueryString();
      } else {
        resetSearchResult();
      }

      searchInput.focus();
    });

    // Event that happens when user submits a search with the form
    searchForm.addEventListener('submit', async(event: SubmitEvent): Promise<void> => {
      event.preventDefault();

      // If the query is different from the one in the search input, clear the result list
      if(!!query && query.length > 0 && query != searchInput.value.trim()) {
        clearResultList();
      }

      // Clear filters
      selectedFilterIds = [];
      selectedCategoryFilterIds = [];

      // Update query string
      query = searchInput.value.trim();
      window.history.pushState({},'', `?${new URLSearchParams({ query }).toString()}`);

      if (query.length > 0) {
        await search();
      } else {
        renderWithoutResult();
      }
    });

    // Event that happens when user clicks the clear filter button
    (document.querySelector(".search-page__filter-clear-button") as HTMLButtonElement).addEventListener('click', async(): Promise<void> => {
      // Clear query string
      window.history.pushState({},'', `?${new URLSearchParams({ query: query ?? '' }).toString()}`);
      
      selectedFilterIds = [];
      selectedCategoryFilterIds = [];
      apiTakeAmount = parseInt(hitsToShow!, 10);
      apiSkipAmount = 0;
      clearResultList();
      await search();
    });
  }
  
  initializeEvents();
};

useSearchPage();
